import { inject, Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { buildQueryParams, createQueryParam } from 'utils/helpers';
import { Registry, RegistryRow, TableFilters } from './registry-details.interface';
import { Observable, Subject } from 'rxjs';
import { ShowIfHttpError } from '@certemy/common';

@Injectable()
export class RegistryDetailsService {
  constructor(private http: HttpService) {}

  @ShowIfHttpError('Cannot receive registry details.')
  getRegistryDetails({ profileId, filters }: { profileId: number; filters: any }) {
    const params = {
      page: filters.page,
      page_size: filters.page_size,
      order_id: filters.order.id,
      order_type: filters.order.type,
      search: filters.search,
      useDefaultRegistryFilters: filters.useDefaultRegistryFilters,
    };
    const queryParams = Object.entries(params).map(([paramKey, paramValue]) => createQueryParam(paramKey, paramValue));
    const query = buildQueryParams(queryParams);

    return this.http.get(`/organization/users/${profileId}/registry-details${query}`);
  }

  @ShowIfHttpError('Cannot receive registry details.')
  getRegistryEditDetails(profileId: number, registryId: number): Observable<RegistryRow.Details> {
    return this.http.get(`/organization/users/${profileId}/registry-details/${registryId}`);
  }

  @ShowIfHttpError('An error occurred while saving registry details.')
  saveRegistryEditDetails(profileId: number, registryId: number, data) {
    return this.http.put(`/organization/users/${profileId}/registry-details/${registryId}`, data);
  }

  @ShowIfHttpError('An error occurred while regenerating file.')
  regenerateFile(registryId: number) {
    return this.http.post(`/certification_instance/regenerate-certification-file/${registryId}`, '');
  }

  changeShowInRegistry(profileId: number, data) {
    return this.http.patch(`/profile/${profileId}`, data);
  }

  checkDuplicates(profileId: number, registryId: number, data) {
    return this.http.post(
      `/organization/users/${profileId}/registry-details/${registryId}/check-number-duplicate`,
      data,
    );
  }

  validatePsvRequest(profileId: number, acronym: string) {
    const data = { profileId, acronym };
    return this.http.post('/psv-request/internal/validate-request', data);
  }

  processPsvRequest(profileId: number, acronym: string) {
    const data = { profileId, acronym };
    return this.http.post('/psv-request/internal/process-request', data);
  }
}

@Injectable()
export class RegistryDetailsResolverService  {
  constructor(private registryDetailsService: RegistryDetailsService) {}

  resolve(route: ActivatedRouteSnapshot) {
    const { profileId } = route.params;
    return this.registryDetailsService.getRegistryDetails({
      profileId: Number(profileId),
      filters: new TableFilters(),
    });
  }
}

export const registryDetailsResolver: ResolveFn<Observable<any>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(RegistryDetailsResolverService).resolve(route);
};

@Injectable()
export class RegistryEditResolverService  {
  constructor(private registryDetailsService: RegistryDetailsService) {}

  resolve(route: ActivatedRouteSnapshot): Observable<RegistryRow.Details> {
    const { profileId, registry_id } = route.params;
    return this.registryDetailsService.getRegistryEditDetails(profileId, registry_id);
  }
}

export const registryEditResolver: ResolveFn<Observable<RegistryRow.Details>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(RegistryEditResolverService).resolve(route);
};

@Injectable()
export abstract class RegistryDetailsBehaviourService {
  usedInModal: boolean;
  registryEditDetailsData$?: Subject<any>;
  onBack$: Subject<boolean>;

  abstract onRowClick: (row: Registry, profileId: number) => void;
}
